import { defineStore } from 'pinia'

import axios from 'axios'

import router from '@/router'

import { getAPIURL } from '@/config'
import { setLanguage } from '@/i18n'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useFlowStore } from '@/stores/FlowStore'
import { useCameraStreamStore } from '@/stores/CameraStreamStore'
import { useDetectionZoneStore } from '@/stores/DetectionZone'


const API_URL = getAPIURL()

export const useUserStore = defineStore({
    id: 'user',

    state: () => ({
        users: [],

        // legacy shape for backwards compat:
        user: {
            UserId: null,
            UserName: null,
            UserSurname: null,
            UserEmail: null,
            UserRole: null,
            CompanyId: null,
        },
        
        // new multi-tenant fields:
        isSuperAdmin: false,
        companies: [],       // full access[] map from server
        currentCompany: {},  // defaultCompany from server

        error: null,
        token: localStorage.getItem('authToken') || null,
        loadingUserDetails: false,
    }),

    getters: {
        isUserLoggedIn: (state) => {
            return !!state.token; // Ensure token is present
        },

        // are they the plain User role in the active tenant?
        isRegularUser: (state) =>
            !state.isSuperAdmin && state.currentCompany.role === 'User',

        // are they a Key User?
        isKeyUser: (state) =>
            !state.isSuperAdmin && state.currentCompany.role === 'Key User',

        // are they an Admin (company-admin or global super)?
        isAdminInCurrentCompany: (state) =>
            state.isSuperAdmin || state.currentCompany.role === 'Admin',

        // a partner “Super User” = key_user in *all* their companies
        isSuperUser: state => {
            // not a global super-admin, and has 2+ entries,
            // and every entry’s role is 'key_user'
            return !state.isSuperAdmin
                && Array.isArray(state.companies)    
                && state.companies.length > 1
                && state.companies.every(c => c.role === 'Key User')
        }
    },

    actions: {
        async addUser(userData) {
            try {
                const response = await axios.post(`${API_URL}/add_user`, userData)
                return response.data
            }
            catch (error) {
                this.error = error.response.data.message // Capture the error message
                throw new Error(error.response.data.message) // Throw it as part of the error
            }
        },

        // The people who work with a company, with the role they hold THERE.
        // Replaces company.users, which was the FK list (whoever was currently
        // switched to the company) rather than its actual membership.
        async fetchCompanyMembers(companyId) {
            try {
                const { data } = await axios.get(`${API_URL}/company/${companyId}/members`)
                return data
            }
            catch (error) {
                const msg = error.response?.data?.message || 'Could not load team members'
                this.error = msg
                throw new Error(msg)
            }
        },

        // Allen push opt-in (docs/15 C) — ADMIN ONLY, so its own gated endpoint
        // (not the any-role /user/preferences whitelist). Persisted on the
        // membership; get_user_details returns it read-only in `preferences`.
        async setCoachPush(enabled) {
            const { data } = await axios.put(`${API_URL}/user/coach-push`,
                { CoachPushEnabled: !!enabled })
            if (this.user?.preferences) this.user.preferences.CoachPushEnabled = data.CoachPushEnabled
            return data.CoachPushEnabled
        },

        // Real-time zone opt-in for the active company (docs/15 channel 2).
        // Returns { subscribedZoneIds, customised }. customised=false means the
        // user hasn't narrowed it → treat as all zones.
        async fetchZoneSubscriptions() {
            const { data } = await axios.get(`${API_URL}/user/zone-subscriptions`)
            return data
        },
        async updateZoneSubscriptions(zoneIds) {
            const { data } = await axios.put(`${API_URL}/user/zone-subscriptions`, { zoneIds })
            return data
        },

        // Give an EXISTING Storalytic user a membership of a company. Super-admin
        // only (the backend enforces it): a store admin only knows their own
        // people, so letting them test whether an email exists would be an
        // account-enumeration oracle.
        async addExistingMember(companyId, userEmail, userRole) {
            try {
                const { data } = await axios.post(
                    `${API_URL}/company/${companyId}/members`,
                    { UserEmail: userEmail, UserRole: userRole })
                return data
            }
            catch (error) {
                const msg = error.response?.data?.message || 'Could not add this user'
                this.error = msg
                throw new Error(msg)
            }
        },

        async resendInvite(userId) {
            try {
                const { data } = await axios.post(`${API_URL}/users/${userId}/resend-invite`, {})
                return data
            }
            catch (error) {
                const msg = error.response?.data?.message || 'Could not re-send the invitation'
                this.error = msg
                throw new Error(msg)
            }
        },

        // Persist a subset of the caller's own preferences (mail reports and/or
        // Allen notification e-mails). Merge-safe on the backend — never clobbers
        // the access map. Pass only the keys you're changing; returns the full
        // merged preferences object.
        async updatePreferences(prefs) {
            try {
                const { data } = await axios.put(`${API_URL}/user/preferences`, prefs)
                if (this.user) this.user.preferences = data.preferences
                // keep the cached copy in sync so a reload doesn't revert
                try {
                    const cached = JSON.parse(localStorage.getItem('userDetails') || '{}')
                    cached.preferences = data.preferences
                    localStorage.setItem('userDetails', JSON.stringify(cached))
                } catch (_) { /* cache best-effort */ }
                return data.preferences
            }
            catch (error) {
                const msg = error.response?.data?.message || 'Could not save preferences'
                this.error = msg
                throw new Error(msg)
            }
        },

        // The caller's own language ('en' | 'nl'), or null to follow the store's
        // default. Per person: it holds in every store they belong to.
        async setMyLanguage(code) {
            const { data } = await axios.put(`${API_URL}/user/language`, { language: code })
            this._applyLanguage(data)
            return data.language
        },

        // The current store's default for members who haven't chosen. Admin only.
        async setCompanyDefaultLanguage(code) {
            const { data } = await axios.put(`${API_URL}/company/default-language`, { language: code })
            // Our own resolved language may follow it: re-read rather than guess.
            await this.fetchUserDetails()
            return data.companyDefaultLanguage
        },

        _applyLanguage({ language, languagePreference, companyDefaultLanguage }) {
            const lang = setLanguage(language)
            if (this.user) {
                this.user.language = lang
                this.user.languagePreference = languagePreference ?? null
                this.user.companyDefaultLanguage = companyDefaultLanguage ?? null
            }
            try {
                const cached = JSON.parse(localStorage.getItem('userDetails') || '{}')
                Object.assign(cached, { language: lang, languagePreference, companyDefaultLanguage })
                localStorage.setItem('userDetails', JSON.stringify(cached))
            } catch (_) { /* cache best-effort */ }
        },

        async updateUser(user) {
            try {
                await axios.put(`${API_URL}/users/${user.UserId}`, user, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                })
            } catch (error) {
                throw new Error('Failed to update user')
            }
        },

        async deleteUser(userEmail) {
            try {
                const response = await axios.delete(`${API_URL}/delete_user`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    },
                    data: { userEmail }
                })
                return response.data
            } catch (error) {
                this.error = error.response?.data?.message || 'An error occurred'; // Capture the error message
                throw new Error(this.error) // Throw it as part of the error
            }
        },

        // Expire the httpOnly device_session cookie server-side (known-gaps #3,
        // Stage 1). JavaScript CANNOT clear an httpOnly cookie, so without this
        // round trip a signed-out browser keeps live-camera access at
        // /device/<id>/ until the cookie expires on its own (up to 12h).
        //
        // Never allowed to block the sign-out. If the portal is unreachable the
        // user must still end up logged out locally; the cookie then lapses on
        // its own schedule. That is the same trade-off _record_login_attempt
        // makes on the way in: auditing (or here, cleanup) must not lock someone
        // out of their own session.
        async logoutServerSide() {
            try {
                await axios.post(`${API_URL}/logout`, {}, { withCredentials: true })
            } catch (error) {
                console.warn('Device-session logout failed; clearing locally anyway', error)
            }
        },

        async loginUser(credentials) {
            console.log('Attempting login with credentials')
            const response = await axios.post(`${API_URL}/login`, credentials)
            this.token = response.data.token // Store the received token
            console.log('Login successful, token received:')
            localStorage.setItem('authToken', this.token); // Store token in local storage
            await this.fetchUserDetails()
        },

        async changePassword({ oldPassword, newPassword }) {
            try {
                await axios.put(
                `${API_URL}/users/change-password`,
                { oldPassword, newPassword },
                { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
                )
            } catch (error) {
                const msg = error.response?.data?.message || 'Change password failed'
                throw new Error(msg)
            }
        },

        async forgotPassword({ email }) {
            try {
                await axios.post(`${API_URL}/users/forgot-password`, { email })
            } catch (error) {
                const msg = error.response?.data?.message || 'Error initiating password reset'
                throw new Error(msg)
            }
        },

        async resetPassword({ token, newPassword }) {
            try {
                await axios.put(`${API_URL}/users/reset-password`, { token, newPassword })
            } catch (error) {
                const msg = error.response?.data?.message || 'Reset password failed'
                throw new Error(msg)
            }
        },

        async fetchUserDetails() {
            console.log('Checking if user details are stored in browser memory...')
            const flowStore = useFlowStore()
            const companyStore = useCompanyStore()
            const cameraStreamStore = useCameraStreamStore()
            const detectionZoneStore = useDetectionZoneStore()
        
            // 1) Make sure we have a token
            const token = localStorage.getItem('authToken')
            if (!token) {
                console.log('No token found. Redirecting to login...')
                localStorage.removeItem('FixedWidgetsData')
                detectionZoneStore.clearDetectionZones()
                cameraStreamStore.clearStreams()
                flowStore.clearFlows()
                this.clearUserState()
                companyStore.clearCompanyState()
                return null
            }

            // 2) Restore cached userDetails if present (but DO NOT return early)
            const storedUser = localStorage.getItem('userDetails')
            
            //If user exists in localStorage, use them
            if (storedUser) {
                console.log('User details found in localStorage')
                const d = JSON.parse(storedUser)
                // **restore in-memory store from cache**
                this.user = {
                    UserId:     d.UserId,
                    UserName:   d.UserName,
                    UserSurname:d.UserSurname,
                    UserEmail:  d.UserEmail,
                    UserRole:   d.currentCompany.role,
                    CompanyId:  d.currentCompany.CompanyId
                }
                this.isSuperAdmin    = d.isSuperAdmin
                this.companies       = d.access
                this.currentCompany  = d.currentCompany
                if (d.language) setLanguage(d.language)
            // **DON’T return here** — we still want to re‑fetch fresh data below
            }
        
            // 3) Now always do the API call so that we pick up any changes
            this.loadingUserDetails = true
            try {
                console.log('Fetching user details from API...')

                // Perform the API call to fetch user details
                const response = await axios.get(`${API_URL}/get_user_details`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                })
        
                // response.data now includes:
                // { UserId, UserName, UserSurname, UserEmail,
                //   isSuperAdmin, defaultCompany:{CompanyId,role},
                //   access:[{CompanyId,role},…] }
                const d = response.data

                // **populate in-memory store with fresh API data**
                this.user = {
                    UserId: d.UserId,
                    UserName:  d.UserName,
                    UserSurname: d.UserSurname,
                    UserEmail: d.UserEmail,
                    UserRole: d.defaultCompany.role,
                    CompanyId:d.defaultCompany.CompanyId,
                    // Self-service preferences: mail reports + Allen notification
                    // e-mails. One object, persisted via updatePreferences().
                    preferences: d.preferences || {
                        SendEndOfDay: false, SendEndOfWeek: false, SendEndOfMonth: false,
                        CoachEmailEnabled: false, CoachEmailHigh: true,
                        CoachEmailWarning: true, CoachEmailGood: false,
                    }
                }
                
                // Language (user's choice -> store default -> English, resolved by
                // the backend). The two raw values are for Settings: own choice vs
                // following the store.
                this.user.language = setLanguage(d.language)
                this.user.languagePreference = d.languagePreference ?? null
                this.user.companyDefaultLanguage = d.companyDefaultLanguage ?? null

                // new multi‐tenant fields:
                this.isSuperAdmin = d.isSuperAdmin
                this.companies = d.access
                this.currentCompany = d.defaultCompany

                // **overwrite the cache** with this fresh data
                localStorage.setItem('userDetails', JSON.stringify({
                    ...d,
                    currentCompany: d.defaultCompany
                }))
                
                //console.log('User details fetched successfully and saved in localStorage:', this.user)
                console.log('User details fetched successfully and saved in localStorage:')
        
                return this.user // Return the user details after they are fetched
            } catch (error) {
                console.error('Fetching user details failed:', error)
        
                if (error.response?.status === 401) {
                    console.error('Unauthorized access - token expired or invalid')
                    localStorage.removeItem('FixedWidgetsData')
                    detectionZoneStore.clearDetectionZones()
                    cameraStreamStore.clearStreams()
                    flowStore.clearFlows()
                    this.clearUserState()
                    companyStore.clearCompanyState()
                    router.push('/login')
                }
        
                return null // Return null if fetching user details failed
            } finally {
                this.loadingUserDetails = false
            }
        },

        // new: switch company and re-issue token
        async selectCompany(companyId) {
            // hit your endpoint that returns a new JWT including the new tenant
            const { data } = await axios.post(
                `${API_URL}/select-company`,
                { CompanyId: companyId },
                { headers: { Authorization: `Bearer ${this.token}` } }
            )
            // store new token
            this.token = data.token
            localStorage.setItem('authToken', this.token)
            axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`

            // update the store: if global super-admin, they see *all* companies,
            // so we just synthesize a {CompanyId,role} entry ourselves
            let picked
            if (this.isSuperAdmin) {
                picked = { CompanyId: companyId, role: 'Admin' }
            } else {
                picked = this.companies.find(c => c.CompanyId === companyId)
            }
            // Guard: a super-admin synthesizes their entry, but anyone else
            // switching to a company they aren't a member of would blow up on
            // picked.role below. Fail loudly instead of writing `undefined`.
            if (!picked) {
                throw new Error(`No membership of company ${companyId}`)
            }
            this.currentCompany = picked
            this.user.UserRole  = picked.role
            this.user.CompanyId = picked.CompanyId

            // Preferences are PER-COMPANY now, so the ones we hold belong to the
            // company we just left. Drop them rather than briefly showing another
            // store's settings; the next fetchUserDetails() loads this store's.
            this.user.preferences = null

            // update cached userDetails blob so future fetchUserDetails() doesn’t revert
            const fullShape = JSON.parse(localStorage.getItem('userDetails') || '{}')
            fullShape.currentCompany = picked
            delete fullShape.preferences
            localStorage.setItem('userDetails', JSON.stringify(fullShape))
        },

        clearUserState() {
            console.log('Clearing user state...')
            this.user = {
                UserId: null,
                UserName: '',
                UserSurname: '',
                UserRole: '',
                UserEmail: '',
                CompanyId: null
                // reset other relevant properties
            }
            this.isSuperAdmin   = false
            this.companies      = []
            this.currentCompany = {}
            localStorage.removeItem('userDetails')
            this.token = null; // Clear the token
            localStorage.removeItem('authToken')
            //console.log('User state cleared:', this.user)
            console.log('User state cleared:')
        }
    },

    // Watch token change to ensure reactivity
    watch: {
        token(newToken) {
            console.log('Token changed:', newToken)
            if (newToken) {
                localStorage.setItem('authToken', newToken)
            } else {
                localStorage.removeItem('authToken')
            }
        }
    }
})